import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Heart, Users, TrendingUp, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useJsonLd } from '@/hooks/useJsonLd';

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "Our Story | KGOB CPAs & Advisors",
  "url": "https://kgob.com/our-story",
  "description": "KGOB's journey from a small North Carolina firm in 1988 to a trusted financial partner for growth-minded clients across the United States.",
  "mainEntity": {
    "@type": "AccountingService",
    "name": "KGOB CPAs & Advisors",
    "alternateName": "Kohari Gonzalez Oneyear & Brown",
    "legalName": "Kohari Gonzalez Oneyear & Brown PLLC",
    "foundingDate": "1988",
    "slogan": "Let's Talk Growth",
    "url": "https://kgob.com",
    "logo": "https://kgob.com/brand/kgob-logo.png",
    "telephone": "+1-844-599-3355",
    "email": "connect@kgob.com",
    "address": { "@type": "PostalAddress", "streetAddress": "2740 East WT Harris Blvd, Suite 200", "addressLocality": "Charlotte", "addressRegion": "NC", "postalCode": "28213", "addressCountry": "US" },
    "areaServed": { "@type": "Country", "name": "United States" }
  }
};

const OurStory = () => {
  useJsonLd('our-story-about', ABOUT_SCHEMA);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Our Story | KGOB CPAs & Advisors</title>
        <meta name="description" content="Learn about KGOB's journey from a small North Carolina firm in 1988 to a trusted financial partner for growth-minded clients across the United States." />
        <link rel="canonical" href="https://kgob.com/our-story" />
        <meta property="og:title" content="Our Story | KGOB CPAs & Advisors" />
        <meta property="og:description" content="KGOB's journey from a small North Carolina firm in 1988 to a trusted financial partner serving clients across the United States." />
        <meta property="og:url" content="https://kgob.com/our-story" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Our Story, Our Mission, Our Promise
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic">
                A legacy of honest work, careful numbers, and steady guidance since 1988
              </p>
            </motion.div>
          </div>
        </section>

        {/* A Quiet Beginning */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              A Quiet Beginning in the Carolinas
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-[#4C5A70] mb-4">
                Our story began long before the KGOB name existed.
              </p>
              
              <p className="text-[#4C5A70] mb-4">
                In 1988, a small North Carolina accounting firm opened its doors with a simple commitment: to serve families and business owners with honest work, careful numbers, and steady guidance they could rely on year after year.
              </p>
              
              <p className="text-[#4C5A70] mb-4">
                Over time, that legacy practice grew, adapted, and changed hands—but the heart of it stayed the same: take care of people, do excellent work, and always tell the truth about the numbers.
              </p>
              
              <p className="text-[#4C5A70] mb-4">
                On February 1, 2022, that legacy formally evolved into <strong>Kohari Gonzalez Oneyear & Brown PLLC (KGOB CPAs & Advisors)</strong>, bringing together seasoned professionals, expanded locations in Charlotte, Winston-Salem, and Mooresville, and a renewed focus on helping growth-minded clients navigate an increasingly complex financial world.
              </p>
              
              <p className="text-[#4C5A70]">
                We didn't create KGOB to chase headlines or scale at all costs. We built it to be a calm, steady firm that people can count on—especially when the stakes feel high.
              </p>
            </div>
            
            <Card className="mt-8 p-6 border-2 border-[#E1DFD7] bg-[#F7F6F2]">
              <p className="text-sm text-[#4C5A70] text-center mb-4">
                <strong>Prefer a printable version?</strong>
              </p>
              <p className="text-sm text-[#4C5A70] text-center mb-4">
                You can download our KGOB Firm Overview (PDF) to share with partners, lenders, or anyone you'd like to introduce to our firm.
              </p>
              <div className="flex justify-center">
                <Button data-testid="our-story-button-4" asChild className="bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <a data-testid="our-story-a-1" href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/firm-overview/download`} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" />
                    Download Firm Overview (PDF)
                  </a>
                </Button>
              </div>
            </Card>
          </div>
        </section>

        {/* Why We Exist */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Why We Exist
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-[#4C5A70] mb-4">
                Money is never just numbers.
              </p>
              
              <p className="text-[#4C5A70] mb-4">
                It's the business you've built from scratch, the payroll you feel responsible for every two weeks, the retirement you're hoping to protect, the family you want to take care of, and the quiet fear of "What did I miss?" when it comes to taxes and compliance.
              </p>
              
              <p className="text-[#4C5A70] mb-6">
                Too many people feel like they're carrying that weight alone.
              </p>
              
              <p className="text-[#4C5A70] mb-6">
                <strong>KGOB exists to change that.</strong>
              </p>
              
              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <p className="text-[#0B2240] text-lg font-medium">
                  KGOB exists to create extraordinary value for its clients, people, and community by pairing high-quality, comprehensive tax and accounting services with proactive advice that eases their burden, protects their wealth, and supports long-term financial success.
                </p>
              </Card>
              
              <p className="text-[#4C5A70] mt-6">
                We see ourselves as a financial nerve center for our clients—a calm, expert team they can lean on year-round for clarity, confidence, and better decisions, not just a once-a-year tax stop.
              </p>
            </div>
          </div>
        </section>

        {/* The People Behind the Practice */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              The People Behind the Practice
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-[#4C5A70] mb-6">
                KGOB is led by <strong>Sara F. Gonzalez, CPA, Managing Partner</strong>, who brings more than 25 years of experience in public and private accounting across a wide range of industries.
              </p>
              
              <p className="text-[#4C5A70] mb-4">
                Sara did not set out to build a flashy firm. Her vision has always been more grounded and personal:
              </p>
              
              <div className="space-y-3 mb-6 ml-6">
                <div className="flex gap-3">
                  <Heart className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <p className="text-[#4C5A70]">A place where clients feel respected and heard, not rushed or talked down to.</p>
                </div>
                <div className="flex gap-3">
                  <Heart className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <p className="text-[#4C5A70]">A team that can take a complex situation, sit down with it quietly, and come back with a clear, practical plan.</p>
                </div>
                <div className="flex gap-3">
                  <Heart className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <p className="text-[#4C5A70]">A firm that treats every engagement—large or small—as an opportunity to protect someone's future, not just complete a task.</p>
                </div>
              </div>
              
              <p className="text-[#4C5A70] mb-4">
                Under her leadership, KGOB has grown into a multi-location, full-service CPA firm that combines:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <Card className="p-6 border-2 border-[#E1DFD7]">
                  <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Luxury Advisory</h3>
                  <p className="text-[#4C5A70] text-sm">Thoughtful, high-touch guidance when the decisions are complex or the stakes are high.</p>
                </Card>
                
                <Card className="p-6 border-2 border-[#E1DFD7]">
                  <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Precision Compliance</h3>
                  <p className="text-[#4C5A70] text-sm">Meticulous tax, accounting, and reporting work so the details are handled and you can breathe a bit easier.</p>
                </Card>
              </div>
              
              <p className="text-[#4C5A70]">
                Behind Sara is a team of professionals who share the same quiet, steady approach: do the work right, explain it clearly, and always keep the client's best interests at the center.
              </p>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Who We Serve
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7] bg-white mb-8">
              <p className="text-[#4C5A70] mb-6">
                We primarily serve <strong>growth-minded individuals and closely held businesses</strong>—the people in the middle of the real work of running companies, creating jobs, and supporting families.
              </p>
              
              <p className="text-[#4C5A70] mb-4 font-medium">Many of our best-fit clients are:</p>
              
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">Small business owners across the United States who have outgrown DIY bookkeeping or basic tax prep.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">Families and individuals with increasingly complex tax situations who want proactive, year-round guidance—not last-minute surprises.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">Business owners who are thinking ahead about growth, succession, or eventual exit, and know that clean numbers and smart planning matter.</p>
                </div>
              </div>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg">
                <p className="text-[#4C5A70] mb-4">
                  We are selective about fit—not to be exclusive for its own sake, but to protect the level of service and responsiveness we provide.
                </p>
                <p className="text-[#4C5A70] font-medium">We do our best work with clients who:</p>
                <div className="space-y-2 mt-3">
                  <p className="text-[#4C5A70] text-sm">• Value long-term relationships over one-off transactions</p>
                  <p className="text-[#4C5A70] text-sm">• Are willing to invest in doing things the right way</p>
                  <p className="text-[#4C5A70] text-sm">• Treat our team with the same respect and professionalism we extend to them</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* How We Work: Quiet Excellence */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              How We Work: Quiet Excellence, People First
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12">
              At KGOB, our culture is built around a few simple but non-negotiable ideas.
            </p>
            
            <div className="space-y-8">
              {[
                {
                  number: '1',
                  title: 'People First, Always',
                  description: 'We care about how financial decisions feel, not just how they look on a spreadsheet. Clients come to us with real fears—IRS letters, payroll worries, messy books, complex life transitions—and our first job is to listen.'
                },
                {
                  number: '2',
                  title: 'Calm in Complexity',
                  description: 'We encourage clients to bring us their "messy." Conflicting records, years of catch-up, multi-entity situations, or IRS issues—these are the realities of real businesses and real lives. Our role is to bring structure and calm to that complexity, not judgment.'
                },
                {
                  number: '3',
                  title: 'Proactive, Not Just Reactive',
                  description: 'We are planners, not just processors. We focus on year-round strategy, not simply filing returns at the last minute. Whenever possible, we want to help clients move from "putting out fires" to operating with a thoughtful, forward-looking plan.'
                },
                {
                  number: '4',
                  title: 'Integrity in Every Decision',
                  description: 'We adhere to professional standards and do the right thing, even when it\'s not the easiest or most profitable choice. That includes being honest when we\'re not the right fit—and pointing people in a better direction when needed.'
                },
                {
                  number: '5',
                  title: 'Teachers at Heart',
                  description: 'We believe people make better decisions when they truly understand their numbers. Whether we\'re explaining a tax strategy, walking through financial statements, or interpreting an IRS notice, our goal is to make it understandable, not overwhelming.'
                },
                {
                  number: '6',
                  title: 'Community Matters',
                  description: 'From business networking breakfasts to Toys for Tots events and educational initiatives like the "Let\'s Talk" platforms, we see our work as part of a larger commitment to the communities we serve.'
                }
              ].map((principle, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{principle.number}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{principle.title}</h3>
                      <p className="text-[#4C5A70] text-sm">{principle.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones That Shaped Us */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              Milestones That Shaped Us
            </h2>
            
            <div className="space-y-6">
              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">1988 – A Legacy Begins</h3>
                <p className="text-[#4C5A70]">
                  A North Carolina accounting firm is founded, laying the groundwork for what will eventually become KGOB CPAs & Advisors. The focus from the start: careful work, honest advice, and long-term client relationships.
                </p>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">February 1, 2022 – A New Chapter as KGOB</h3>
                <p className="text-[#4C5A70]">
                  Transition to current ownership and rebrand as Kohari Gonzalez Oneyear & Brown PLLC, with an expanded presence in Charlotte, Winston-Salem, and Mooresville and a renewed commitment to high-touch, relationship-based service.
                </p>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">2023–2024 – Navigating Succession and Renewal</h3>
                <p className="text-[#4C5A70]">
                  This period marked an important chapter of succession and renewal for the firm. As prior leadership moved toward retirement and new leadership stepped forward, we focused on stabilizing the practice, modernizing key systems, and reaffirming our commitment to high-touch, relationship-based service. We used this time to sharpen our understanding of the clients we serve best and to strengthen the internal processes that support them, so that our next decades would rest on a solid, carefully built foundation.
                </p>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">2024–2025 – Strengthening Our Foundation</h3>
                <p className="text-[#4C5A70] mb-4">
                  During this chapter, we invested heavily in our infrastructure and internal systems. We transitioned to a modern payroll and compliance platform, formalized a more structured onboarding experience through a dedicated client intake system, and expanded our ability to pull and interpret IRS transcripts so we can respond more quickly and precisely when tax issues arise.
                </p>
                <p className="text-[#4C5A70]">
                  Just as importantly, we honed our team and workflows—clarifying roles, tightening processes, and building a culture that supports responsive, high-quality work for the right clients. We emerged from this period leaner, clearer, and more confident in the kind of firm we are building.
                </p>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-3">2025 and Beyond – Quiet, Intentional Growth</h3>
                <p className="text-[#4C5A70]">
                  With our people, systems, and client focus better aligned, we are now positioned for steady, intentional growth—not by becoming all things to all people, but by serving a well-defined group of clients at a deeper level, and continuing to build tools and resources that make complex financial decisions easier to navigate.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* The Work We Do */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              The Work We Do (At a Glance)
            </h2>
            
            <p className="text-[#4C5A70] text-center mb-12">
              While this page is about who we are, not a full service menu, it may help to understand the core of our work.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                'Bookkeeping & Small Business Accounting',
                'Payroll Services & Payroll Tax Compliance',
                'Business & Individual Tax Preparation and Planning',
                'IRS Representation & Tax Problem Resolution',
                'Strategic Tax Planning & Business Advisory',
                'Assurance Services (Audit, Review, Compilation)'
              ].map((service, i) => (
                <div key={i} className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70] text-sm"><strong>{service}</strong></p>
                </div>
              ))}
            </div>
            
            <p className="text-[#4C5A70] text-center mb-6">
              Across all these areas, our goal stays the same: bring clarity, reduce stress, and support better long-term decisions.
            </p>
            
            <p className="text-sm text-[#4C5A70] text-center">
              For more detail on what it's like to work with us day to day, you can visit our <Link data-testid="our-story-link-4" to="/how-we-work" className="text-[#D4AF37] hover:underline font-medium">How We Work</Link> page.
            </p>
          </div>
        </section>

        {/* Our Vision for the Future */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Our Vision for the Future
            </h2>
            
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-[#4C5A70] mb-6">
                Looking ahead, we see KGOB continuing to grow—not as a volume-driven firm, but as a trusted financial home base for clients who share our values.
              </p>
              
              <p className="text-[#4C5A70] mb-4 font-medium">We envision:</p>
              
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">More clients who treat us as a year-round partner, not just tax season help</p>
                </div>
                <div className="flex gap-3">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">More tools and platforms that make complex information easier to understand and act on</p>
                </div>
                <div className="flex gap-3">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">More opportunities to teach, mentor, and support our community in practical, down-to-earth ways</p>
                </div>
                <div className="flex gap-3">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">A team that continues to develop professionally while staying grounded in humility, integrity, and service</p>
                </div>
              </div>
              
              <p className="text-[#4C5A70] mb-4 font-medium">If we do our work well, the result won't just be cleaner books or lower tax bills. It will be:</p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                  <Users className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#4C5A70] text-sm">Business owners who sleep better at night</p>
                </Card>
                
                <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                  <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#4C5A70] text-sm">Families who feel more secure</p>
                </Card>
                
                <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                  <TrendingUp className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#4C5A70] text-sm">Communities that benefit from stronger, more stable local businesses</p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Our Mission, in a Single Line
            </h2>
            
            <Card className="p-10 border-2 border-[#D4AF37] bg-gradient-to-br from-white to-[#F7F6F2]">
              <p className="font-['Playfair_Display'] text-2xl text-[#0B2240] text-center leading-relaxed">
                KGOB exists to be the calm, trusted financial nerve center for growth-minded clients—quietly pairing luxury-level advisory with precise, ethical compliance so they can move forward with clarity, confidence, and peace of mind.
              </p>
            </Card>
          </div>
        </section>

        {/* What This Means for You */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              What This Means for You
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7] bg-white">
              <p className="text-[#4C5A70] mb-6">
                If you are reading this as a potential client, here is what you can expect from us:
              </p>
              
              <div className="space-y-3">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">We will listen before we recommend.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">We will take your situation seriously, whether it is simple or extremely complex.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">We will be honest if we are not the right fit—and grateful if we are.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">We will work hard behind the scenes so that, on your side of the relationship, things feel calmer, clearer, and more manageable.</p>
                </div>
              </div>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg mt-6">
                <p className="text-[#0B2240] text-center font-medium">
                  For us, "success" is not just a completed return or balanced set of books. It is the moment you exhale and realize you don't have to carry all of this by yourself anymore. That's the work we quietly show up to do, every day.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Where to Go Next */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-8">
              Where to Go Next
            </h2>
            
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              To see how this story translates into your day-to-day experience as a client, you can visit our How We Work page.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="our-story-button-3" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="our-story-link-3" to="/how-we-work">See How We Work</Link>
              </Button>
              <Button data-testid="our-story-button-2" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="our-story-link-2" to="/book-consultation?source=our-story">Start a Conversation</Link>
              </Button>
              <Button data-testid="our-story-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="our-story-link-1" to="/services">Explore Our Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default OurStory;